import {
  ApiAttributionModel,
  ApiMetric,
  ApiMetricCategory,
  ApiSimpleMetricLabel,
} from "@incendium/api";
import {
  Autocomplete,
  Box,
  Button,
  OutlinedTextFieldProps,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { StyledTooltip } from "Components/UI/StyledTooltip";
import { useMetricLabels } from "features/analytics/hooks/useMetricLabels";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import {
  metricCategoryTitle,
  orderGroupedMetricLabels,
} from "features/analytics/utils/utils";
import { useChartDescriptionDrawer } from "Providers/ChartDescriptionDrawerProvider";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { metricConfigByName } from "../services/metricFormatter";
import {
  getAttributionMapping,
  metricByAttribution,
} from "../services/attributionMapping";
import { isEmpty } from "Helpers/isEmpty";

interface IMetricSelectorProps extends Omit<
  OutlinedTextFieldProps,
  "onChange" | "variant"
> {
  metric: ApiMetric | null;
  initialMetric?: ApiMetric;
  label?: string;
  filterFn?: (v: ApiSimpleMetricLabel) => boolean;
  onChange: (e: SyntheticEvent, v: ApiSimpleMetricLabel) => void;
  hasControls?: boolean;
  overrideIsStatic?: boolean;
}

const popularMetrics = [
  ApiMetric.METRIC_SESSIONS_COUNT,
  ApiMetric.METRIC_LEADS_COUNT,
  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
  ApiMetric.METRIC_REVENUE,
  ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
  ApiMetric.METRIC_AVERAGE_SALE_VALUE,
  ApiMetric.METRIC_CAC,
  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
  ApiMetric.METRIC_BOUNCE_RATE,
  ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
];

// Helper function to check if a metric is a static attribution metric
const isStaticAttributionMetricFn =
  (force: boolean) =>
  (metric: ApiMetric): boolean => {
    const attributionMapping = getAttributionMapping(metric);
    if (force) {
      return !isEmpty(attributionMapping);
    }

    return (
      attributionMapping.attribtionType !== undefined &&
      attributionMapping.attribtionType !==
        ApiAttributionModel.NO_MODEL_SELECTED
    );
  };

// Helper function to get the base metric from a static attribution metric
const getBaseMetricFromStatic = (metric: ApiMetric): ApiMetric | null => {
  const attributionMapping = getAttributionMapping(metric);
  if (attributionMapping.attribtionMetric) {
    // Return the base metric (NO_MODEL_SELECTED version)
    return (
      metricByAttribution(
        ApiAttributionModel.NO_MODEL_SELECTED,
        attributionMapping.attribtionMetric,
      ) || null
    );
  }
  return null;
};

// Helper function to get static attribution metric from base metric and model
const getStaticAttributionMetric = (
  baseMetric: ApiMetric,
  model: ApiAttributionModel,
): ApiMetric | null => {
  const attributionMapping = getAttributionMapping(baseMetric);
  if (attributionMapping.attribtionMetric) {
    return (
      metricByAttribution(model, attributionMapping.attribtionMetric) || null
    );
  }
  return null;
};

function MetricSelector({
  metric,
  initialMetric,
  label,
  fullWidth,
  filterFn,
  onChange,
  hasControls = true,
  size = "small",
  overrideIsStatic = false,
  ...rest
}: IMetricSelectorProps) {
  const { openDrawer } = useChartDescriptionDrawer();
  const { metricLabels, allMetricLabels, loading } = useMetricLabels();
  const metricToName = useMetricName();
  const [selectedOption, setSelectedOption] = useState<
    ApiSimpleMetricLabel | undefined
  >(undefined);
  const [useStatic, setUseStatic] = useState(
    overrideIsStatic ? overrideIsStatic : false,
  );
  const [selectedAttributionModel, setSelectedAttributionModel] =
    useState<ApiAttributionModel>(ApiAttributionModel.EBA);

  const isStaticAttributionMetric = useCallback(
    (metric: ApiMetric) =>
      isStaticAttributionMetricFn(overrideIsStatic)(metric),
    [overrideIsStatic],
  );

  const popularMetricsLabels: ApiSimpleMetricLabel[] = useMemo(() => {
    return metricLabels
      .filter((m) => popularMetrics.includes(m.metric as ApiMetric))
      .map((m) => ({
        ...m,
        category: "Popular Metrics" as ApiMetricCategory,
      }));
  }, [metricLabels]);

  const parsedMetrics: ApiSimpleMetricLabel[] = useMemo(() => {
    const sorted = orderGroupedMetricLabels(metricLabels);
    const arr = sorted.map((s) => ({
      ...s,
      category: metricCategoryTitle(
        s.category || ApiMetricCategory.METRIC_CATEGORY_UNKNOWN,
      ) as ApiMetricCategory,
    })) as ApiSimpleMetricLabel[];

    return [...popularMetricsLabels, ...arr];
  }, [metricLabels, popularMetricsLabels]);

  const filteredMetrics: ApiSimpleMetricLabel[] = useMemo(() => {
    if (!filterFn) {
      return parsedMetrics;
    }
    return parsedMetrics.filter(filterFn);
  }, [parsedMetrics, filterFn]);

  // Handler for when useStatic checkbox changes
  const handleUseStaticChange = useCallback(
    (checked: boolean) => {
      setUseStatic(checked);

      if (checked && metric && !isStaticAttributionMetric(metric)) {
        // If enabling static attribution on a base metric, get the static version
        const staticMetric = getStaticAttributionMetric(
          metric,
          selectedAttributionModel,
        );
        if (staticMetric) {
          // Find the static metric option and call onChange
          const staticMetricOption = allMetricLabels.find(
            (m) => m.metric === staticMetric,
          );
          if (staticMetricOption) {
            onChange({} as SyntheticEvent, staticMetricOption);
          }
        }
      } else if (!checked && metric && isStaticAttributionMetric(metric)) {
        // If disabling static attribution on a static metric, get the base version
        const baseMetric = getBaseMetricFromStatic(metric);
        if (baseMetric) {
          const baseMetricOption = allMetricLabels.find(
            (m) => m.metric === baseMetric,
          );
          if (baseMetricOption) {
            onChange({} as SyntheticEvent, baseMetricOption);
          }
        }
      }
    },
    [metric, allMetricLabels, onChange, selectedAttributionModel],
  );

  // Handler for when attribution model changes
  const handleAttributionModelChange = useCallback(
    (model: ApiAttributionModel) => {
      setSelectedAttributionModel(model);

      if (useStatic && metric) {
        let baseMetric: ApiMetric;

        if (isStaticAttributionMetric(metric)) {
          // If current metric is already static, get its base metric
          baseMetric = getBaseMetricFromStatic(metric) || metric;
        } else {
          // If current metric is base, use it directly
          baseMetric = metric;
        }

        // Get the new static version with the selected model
        const staticMetric = getStaticAttributionMetric(baseMetric, model);
        if (staticMetric) {
          const staticMetricOption = allMetricLabels.find(
            (m) => m.metric === staticMetric,
          );
          if (staticMetricOption) {
            onChange({} as SyntheticEvent, staticMetricOption);
          }
        }
      }
    },
    [useStatic, metric, allMetricLabels, onChange],
  );

  useEffect(() => {
    const idx = allMetricLabels.findIndex((f) => f.metric === metric);

    if (idx < 0) {
      return;
    }

    const selectedMetric = allMetricLabels[idx];

    // If it's a static attribution metric, show the base metric in the autocomplete
    if (isStaticAttributionMetric(metric as ApiMetric)) {
      const baseMetric = getBaseMetricFromStatic(metric as ApiMetric);
      if (baseMetric) {
        // Find the base metric option
        const baseMetricIdx = allMetricLabels.findIndex(
          (f) => f.metric === baseMetric,
        );
        if (baseMetricIdx >= 0) {
          setSelectedOption(allMetricLabels[baseMetricIdx]);
        } else {
          setSelectedOption(selectedMetric);
        }
      } else {
        setSelectedOption(selectedMetric);
      }

      // Auto-set static attribution controls
      setUseStatic(true);
      const attributionMapping = getAttributionMapping(metric as ApiMetric);
      if (attributionMapping.attribtionType) {
        setSelectedAttributionModel(attributionMapping.attribtionType);
      }
    } else {
      setSelectedOption(selectedMetric);
      setUseStatic(false);
    }
  }, [metric, allMetricLabels]);

  const config = useMemo(
    () => metricConfigByName(metric as ApiMetric),
    [metric],
  );

  // Check if current metric is a static attribution metric
  const isStaticMetric = useMemo(() => {
    if (!metric) return false;
    return isStaticAttributionMetric(metric);
  }, [metric]);

  return (
    <Stack sx={{ width: "100%", overflow: "visible" }} spacing={1}>
      <Autocomplete
        id="y"
        size={size}
        onChange={onChange}
        fullWidth={fullWidth}
        loading={loading}
        options={filteredMetrics}
        groupBy={(option) =>
          option.category || ApiMetricCategory.METRIC_CATEGORY_UNKNOWN
        }
        getOptionLabel={(o) => metricToName(o.metric as ApiMetric)}
        value={selectedOption || { metric: "" as ApiMetric }}
        filterSelectedOptions
        isOptionEqualToValue={(option, value) => option.metric === value.metric}
        disableClearable
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Metric"
            variant="outlined"
            size={size}
            {...rest}
          />
        )}
        renderOption={(props, option) => {
          return (
            <StyledTooltip
              wide
              title={
                <>
                  <Stack px={2} py={1}>
                    <Typography variant="subtitle1" color={"inherit"}>
                      {metricToName(option.metric as ApiMetric)}
                    </Typography>
                    <Typography variant="body1" color={"inherit"}>
                      {option.description || "No description available"}
                    </Typography>
                  </Stack>

                  {option.hasArticle && (
                    <Box mt={1}>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        onClick={() =>
                          openDrawer([], [option.metric as ApiMetric])
                        }
                      >
                        Click to see full description
                      </Button>
                    </Box>
                  )}
                </>
              }
              placement="left"
              arrow
              enterDelay={500}
            >
              <Box component="li" {...props}>
                {metricToName(option.metric as ApiMetric)}
              </Box>
            </StyledTooltip>
          );
        }}
      />
      {/* Show static attribution controls for base metrics or when static metric is selected */}
      {((config.attribtionType &&
        config.attribtionType === ApiAttributionModel.NO_MODEL_SELECTED) ||
        isStaticMetric) && (
        <Stack direction="row" spacing={4}>
          {!overrideIsStatic && (
            <Tooltip
              title={
                <>
                  <Typography color="inherit" variant="body2">
                    The default setting is 'Dynamic' and uses the global
                    attribution model selected at the top of the screen in
                    reports. If you select Static, this metric will be set to a
                    specific model and will not change.
                  </Typography>
                </>
              }
              placement="top"
              arrow
            >
              <ToggleButtonGroup
                value={useStatic ? 1 : 0}
                exclusive
                onChange={(e, v) => {
                  handleUseStaticChange(v === 1);
                }}
                size={size}
              >
                <ToggleButton value={0}>Dynamic</ToggleButton>
                <ToggleButton value={1}>Static</ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          )}
          {useStatic && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              className="metric-selector-static-attribution-controls"
              sx={
                !hasControls
                  ? { flex: 1 }
                  : {
                      position: "absolute",
                      right: 0,
                      width: 260,
                      marginTop: "4px",
                    }
              }
            >
              <Typography variant="subtitle2">Model</Typography>
              <TextField
                size="small"
                fullWidth
                // label="Static Attribution Model"
                value={selectedAttributionModel}
                onChange={(e) => {
                  handleAttributionModelChange(
                    e.target.value as ApiAttributionModel,
                  );
                }}
                select
              >
                <MenuItem value={ApiAttributionModel.EBA}>EBA</MenuItem>
                <MenuItem value={ApiAttributionModel.FIRST}>First</MenuItem>
                <MenuItem value={ApiAttributionModel.LAST}>Last</MenuItem>
                <MenuItem value={ApiAttributionModel.LAST_NON_DIRECT}>
                  Last Non-Direct
                </MenuItem>
                <MenuItem value={ApiAttributionModel.LINEAR}>Linear</MenuItem>
                <MenuItem value={ApiAttributionModel.POSITION_BASED}>
                  Position
                </MenuItem>
                <MenuItem value={ApiAttributionModel.IMPACTED}>
                  Impacted
                </MenuItem>
                <MenuItem value={ApiAttributionModel.CUSTOM}>Custom</MenuItem>
              </TextField>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default MetricSelector;
